import { useCallback, useSyncExternalStore } from 'react';

import { MEDIA } from '../lib/breakpoints';

/**
 * Does the viewport currently match `query`?
 *
 * One `useSyncExternalStore` over `matchMedia`, so every consumer re-renders
 * on the same `change` event and none of them race a resize. The guards exist
 * for jsdom and very old Safari, which expose no MediaQueryList (or one with no
 * EventTarget on it); both fall back to `false`, i.e. desktop.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void): (() => void) => {
      const mql = window.matchMedia?.(query);
      if (!mql || typeof mql.addEventListener !== 'function') return () => undefined;
      mql.addEventListener('change', onChange);
      return () => {
        mql.removeEventListener('change', onChange);
      };
    },
    [query],
  );
  const getSnapshot = useCallback(
    (): boolean => window.matchMedia?.(query).matches ?? false,
    [query],
  );
  return useSyncExternalStore(subscribe, getSnapshot, () => false);
}

/**
 * Is this a handheld — i.e. does the shell become top bar + drawer + tab bar?
 *
 * `MEDIA.handheld`, not `MEDIA.belowLg`: a width test alone missed a phone
 * whose installed PWA was forced into Chrome's desktop-site mode, and a large
 * phone in landscape. See the note on `MEDIA.handheld`.
 */
export function useIsMobile(): boolean {
  return useMediaQuery(MEDIA.handheld);
}

/** A touch screen (or anything else with no precise pointer) — enlarge hit areas. */
export function useIsCoarsePointer(): boolean {
  return useMediaQuery(MEDIA.coarsePointer);
}

/** The OS-level "reduce motion" preference. */
export function useReducedMotion(): boolean {
  return useMediaQuery(MEDIA.reducedMotion);
}
